import { createFileRoute, Link } from '@tanstack/react-router'
import { Building2, MapPin } from 'lucide-react'
import { DashboardLayout } from '@/components/layout'
import { PageHeader, StatTile } from '@/lib/atsUi'
import { DataList } from '@/components/ui/data-list'
import { clients, currentTenant, currentUser } from '@/lib/mocks'
import { atsNav } from '@/lib/atsNav'

export const Route = createFileRoute('/clients/')({
  component: ClientsPage,
})

function ClientsPage() {
  const totalOpenRoles = clients.reduce((sum, c) => sum + c.openRoles, 0)
  const totalActiveCandidates = clients.reduce((sum, c) => sum + c.activeCandidates, 0)

  return (
    <DashboardLayout title="Clients" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <PageHeader
        title="Clients"
        subtitle="The companies you hire for — roles, candidates, and terms at a glance."
      />

      <div className="flex gap-4 mt-6">
        <StatTile label="Clients" value={clients.length} />
        <StatTile label="Open roles" value={totalOpenRoles} />
        <StatTile label="Active candidates" value={totalActiveCandidates} />
      </div>

      <div className="mt-8">
        <DataList
          items={clients}
          getKey={(c) => c.id}
          pageSize={5}
          empty="No clients yet."
          header={
            <div className="hidden md:grid grid-cols-[1fr_160px_80px_120px_60px_72px_160px] gap-2 px-4 py-3 text-xs font-medium text-muted-foreground border-b border-border bg-muted/30">
              <span>Client</span>
              <span>Location</span>
              <span className="text-right">Open roles</span>
              <span className="text-right">Active candidates</span>
              <span className="text-right">Fee %</span>
              <span className="text-right">Guarantee</span>
              <span>Contact</span>
            </div>
          }
          renderRow={(c) => (
            <Link
              to="/clients/$clientId"
              params={{ clientId: c.id }}
              className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/40"
            >
              {/* Mobile layout */}
              <div className="flex flex-col gap-2 w-full md:hidden">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-foreground">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.industry}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                    <MapPin className="h-3 w-3" />
                    {c.location}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-xs text-muted-foreground">Open roles</p>
                    <p className="font-medium text-foreground">{c.openRoles}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Candidates</p>
                    <p className="font-medium text-foreground">{c.activeCandidates}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Fee / Gtee</p>
                    <p className="font-medium text-foreground">{c.feePct}% / {c.guaranteeDays}d</p>
                  </div>
                </div>
                <div className="border-t border-border pt-2">
                  <p className="text-sm text-foreground">{c.contactName}</p>
                  <p className="text-xs text-muted-foreground">{c.contactEmail}</p>
                </div>
              </div>

              {/* Desktop layout */}
              <div className="hidden md:grid grid-cols-[1fr_160px_80px_120px_60px_72px_160px] gap-2 w-full items-center text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate">{c.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{c.industry}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{c.location}</span>
                </div>
                <div className="text-right text-foreground">{c.openRoles}</div>
                <div className="text-right text-foreground">{c.activeCandidates}</div>
                <div className="text-right text-foreground">{c.feePct}%</div>
                <div className="text-right text-foreground">{c.guaranteeDays}d</div>
                <div className="min-w-0">
                  <p className="text-foreground truncate">{c.contactName}</p>
                  <p className="text-xs text-muted-foreground truncate">{c.contactEmail}</p>
                </div>
              </div>
            </Link>
          )}
        />
      </div>
    </DashboardLayout>
  )
}
